import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './SicherheitPage.css';

// =============================================================================
// Konto & Sicherheit — Zweifaktor-Anmeldung einrichten und verwalten.
//
// Der Ablauf ist bewusst dreistufig: QR scannen → mit einem Code bestätigen →
// Wiederherstellungs-Codes sichern. Erst nach der Bestätigung wird beim Login
// gefragt; wer die Einrichtung abbricht, sperrt sich also nicht aus.
// =============================================================================

export default function SicherheitPage() {
  const navigate = useNavigate();

  const [status, setStatus]     = useState(null);
  const [start, setStart]       = useState(null);   // { qr, geheimnis, konto }
  const [code, setCode]         = useState('');
  const [codes, setCodes]       = useState(null);   // einmalig nach Bestätigung
  const [passwort, setPasswort] = useState('');
  const [busy, setBusy]         = useState(false);
  const [msg, setMsg]           = useState(null);

  const token = localStorage.getItem('token');

  const anfrage = useCallback(async (pfad, koerper) => {
    const res = await fetch(`/api/2fa${pfad}`, {
      method: koerper ? 'POST' : 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      ...(koerper ? { body: JSON.stringify(koerper) } : {}),
    });
    const daten = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(daten.error || 'Aktion fehlgeschlagen.');
    return daten;
  }, [token]);

  const laden = useCallback(() => {
    anfrage('/status').then(setStatus).catch(() => setStatus(null));
  }, [anfrage]);

  useEffect(() => {
    if (!token) { navigate('/'); return; }
    laden();
  }, [token, laden, navigate]);

  const einrichtenStarten = async () => {
    setBusy(true); setMsg(null);
    try {
      const d = await anfrage('/start', {});
      setStart(d); setCode('');
    } catch (e) {
      setMsg({ ok: false, text: e.message });
    } finally { setBusy(false); }
  };

  const bestaetigen = async (e) => {
    e.preventDefault();
    setBusy(true); setMsg(null);
    try {
      const d = await anfrage('/bestaetigen', { code: code.trim() });
      setCodes(d.wiederherstellungs_codes);
      setStart(null); setCode('');
      setMsg({ ok: true, text: 'Zweifaktor ist aktiv.' });
      laden();
    } catch (err) {
      setMsg({ ok: false, text: err.message });
    } finally { setBusy(false); }
  };

  const codesErneuern = async () => {
    const c = window.prompt('Zum Erneuern bitte den aktuellen Code aus der App eingeben:');
    if (!c) return;
    setBusy(true); setMsg(null);
    try {
      const d = await anfrage('/codes-neu', { code: c.trim() });
      setCodes(d.wiederherstellungs_codes);
      setMsg({ ok: true, text: 'Neue Codes erzeugt — die alten gelten nicht mehr.' });
      laden();
    } catch (err) {
      setMsg({ ok: false, text: err.message });
    } finally { setBusy(false); }
  };

  const deaktivieren = async (e) => {
    e.preventDefault();
    if (!window.confirm('Zweifaktor wirklich abschalten? Dein Konto ist danach nur noch durch das Passwort geschützt.')) return;
    setBusy(true); setMsg(null);
    try {
      await anfrage('/deaktivieren', { passwort });
      setPasswort(''); setCodes(null);
      setMsg({ ok: true, text: 'Zweifaktor wurde abgeschaltet.' });
      laden();
    } catch (err) {
      setMsg({ ok: false, text: err.message });
    } finally { setBusy(false); }
  };

  const codesKopieren = () => {
    navigator.clipboard?.writeText((codes || []).join('\n'))
      .then(() => setMsg({ ok: true, text: 'Codes in die Zwischenablage kopiert.' }))
      .catch(() => {});
  };

  return (
    <div className="sich-seite">
      <div className="sich-inhalt">
        <button className="sich-zurueck" onClick={() => navigate(-1)}>← Zurück</button>

        <div className="sich-kopf">
          <h1>Konto & Sicherheit</h1>
          <p>Zusätzlicher Schutz für deinen Zugang zu TDA Events.</p>
        </div>

        <div className="sich-karte">
          <div className="sich-karte-kopf">
            <h2>🛡️ Zweifaktor-Anmeldung</h2>
            <span className={`sich-marke ${status?.aktiv ? 'sich-marke-an' : 'sich-marke-aus'}`}>
              {status?.aktiv ? 'aktiv' : 'aus'}
            </span>
          </div>

          {msg && (
            <div className={`sich-meldung ${msg.ok ? 'sich-meldung-ok' : 'sich-meldung-fehler'}`}>
              {msg.text}
            </div>
          )}

          {status?.extern && (
            <p className="sich-text">
              Du bist über die Dojosoftware angemeldet. Der zweite Faktor für
              diesen Zugang wird dort verwaltet.
            </p>
          )}

          {status && !status.moeglich && !status.extern && (
            <p className="sich-text">
              Zweifaktor ist auf diesem Server noch nicht eingerichtet
              (TOTP_KEY fehlt).
            </p>
          )}

          {/* Wiederherstellungs-Codes — erscheinen genau einmal */}
          {codes && (
            <div className="sich-codes">
              <div className="sich-codes-kopf">Wiederherstellungs-Codes</div>
              <p className="sich-text" style={{ marginBottom: 0 }}>
                Jeder Code funktioniert <b>einmal</b> und ersetzt die App — für den Fall,
                dass dein Handy weg ist. Druck sie aus oder leg sie in deinen Passwort-Manager.
                <b> Wir können sie dir nicht noch einmal zeigen.</b>
              </p>
              <ul className="sich-codes-liste">
                {codes.map(c => <li key={c}><code>{c}</code></li>)}
              </ul>
              <div className="sich-aktionen">
                <button className="sich-btn sich-btn-haupt" onClick={codesKopieren}>Kopieren</button>
                <button className="sich-btn sich-btn-still" onClick={() => setCodes(null)}>
                  Gesichert, ausblenden
                </button>
              </div>
            </div>
          )}

          {/* Einrichtung läuft */}
          {start && (
            <>
              <p className="sich-text">
                Scanne den Code mit deiner Authenticator-App (Google Authenticator,
                Authy, 1Password …) und gib danach die sechs Ziffern ein.
              </p>
              <img className="sich-qr" src={start.qr} alt="QR-Code für die Authenticator-App" />
              <details className="sich-manuell">
                <summary>Kamera funktioniert nicht?</summary>
                <p className="sich-text" style={{ margin: '0.6rem 0 0' }}>
                  Dann in der App „manuell eingeben" wählen:
                </p>
                <div className="sich-geheim"><code>{start.geheimnis}</code></div>
                <p className="sich-klein">Konto: {start.konto} · Anbieter: {start.ausgeber}</p>
              </details>
              <form onSubmit={bestaetigen} className="sich-form">
                <input className="sich-code-feld" value={code} onChange={e => setCode(e.target.value)}
                       placeholder="123456" inputMode="numeric" autoComplete="one-time-code" required />
                <button className="sich-btn sich-btn-haupt" type="submit" disabled={busy}>
                  {busy ? 'Prüfe …' : 'Bestätigen'}
                </button>
              </form>
            </>
          )}

          {/* Noch nicht aktiv */}
          {status?.moeglich && !status.aktiv && !start && (
            <>
              <p className="sich-text">
                Mit der Zweifaktor-Anmeldung reicht ein gestohlenes Passwort nicht mehr aus —
                zusätzlich wird ein Code aus deiner Authenticator-App verlangt, der alle
                30 Sekunden wechselt.
              </p>
              <button className="sich-btn sich-btn-haupt" onClick={einrichtenStarten} disabled={busy}>
                {busy ? 'Moment …' : 'Jetzt einrichten'}
              </button>
            </>
          )}

          {/* Aktiv */}
          {status?.aktiv && (
            <>
              <p className="sich-text">
                Aktiv seit {new Date(status.aktiviert_am).toLocaleDateString('de-DE')} ·
                noch <b>{status.codes_offen}</b> Wiederherstellungs-Codes übrig.
                {status.codes_offen <= 2 && <span className="sich-warn"> Wenige übrig — jetzt neue erzeugen.</span>}
              </p>
              <div className="sich-aktionen">
                <button className="sich-btn sich-btn-still" onClick={codesErneuern} disabled={busy}>
                  Neue Wiederherstellungs-Codes
                </button>
              </div>
              <form onSubmit={deaktivieren} className="sich-form sich-form-aus">
                <input type="password" placeholder="Passwort zum Abschalten" autoComplete="current-password"
                       value={passwort} onChange={e => setPasswort(e.target.value)} required />
                <button className="sich-btn sich-btn-gefahr" type="submit" disabled={busy || !passwort}>
                  Abschalten
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
